/**
 * Elite Pre-built Workflow Templates — Customer Success, E-Commerce & Finance
 *
 * Workflows 16-28: Production-grade templates with real API endpoints,
 * AI-powered decisioning, approval gates, and multi-channel notifications.
 *
 * DESIGN RULES (same as prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

export const ELITE_CS_ECOM_FINANCE_WORKFLOWS: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // CUSTOMER SUCCESS (16-20)
  // ===================================================================

  // 16. Customer Support Triage (9 nodes)
  "Customer Support Triage": {
    nodes: [
      trigger("Webhook: Support ticket created", "webhook_app", {
        source: "Zendesk / Freshdesk / Intercom",
        event: "ticket.created",
        howItStarts:
          "A customer submits a new support ticket via your helpdesk",
        setup:
          "In your helpdesk (Zendesk, Freshdesk, Intercom) go to Settings -> Webhooks -> Add endpoint -> paste your workflow URL. The AI will read the raw ticket payload regardless of format.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract ticket details",
        280,
        "Read this helpdesk webhook payload and extract the ticket details. Different helpdesks send data in different formats. Find the customer email, customer name, ticket subject, ticket body, and any priority or tags regardless of field naming. Respond JSON: {customer_email: string, customer_name: string, ticket_subject: string, ticket_body: string, existing_priority: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          { field: "ticket_body", as: "ticket_body", type: "string" },
          {
            field: "existing_priority",
            as: "existing_priority",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "AI classify urgency and category",
        520,
        'You are a support triage specialist. Classify this ticket by urgency and category. Consider keywords like \'down\', \'broken\', \'urgent\', \'billing issue\', \'cancel\' as high urgency. Respond JSON: {urgency: \\"critical\\" | \\"high\\" | \\"medium\\" | \\"low\\", category: \\"billing\\" | \\"technical\\" | \\"account\\" | \\"feature_request\\" | \\"general\\", sentiment: \\"angry\\" | \\"frustrated\\" | \\"neutral\\" | \\"positive\\", suggested_response: string, requires_escalation: \\"true\\" | \\"false\\"}',
        "Subject: {ticket_subject}\\nBody: {ticket_body}\\nCustomer: {customer_name}",
        [
          { field: "urgency", as: "urgency", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
          {
            field: "requires_escalation",
            as: "requires_escalation",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Is urgency critical?", 760, {
        conditionData: [
          {
            variable: "urgency",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #critical-support (urgent)",
        1000,
        "🚨 *CRITICAL TICKET* from {customer_name} ({customer_email})\\nSubject: {ticket_subject}\\nSentiment: {sentiment}\\nCategory: {category}\\n\\nSuggested response: {suggested_response}",
      ),
      n(
        "6",
        "condition",
        "Requires human escalation?",
        1000,
        {
          conditionData: [
            {
              variable: "requires_escalation",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
        460,
      ),
      n(
        "7",
        "approvalGate",
        "Manager reviews escalation",
        1240,
        {
          approvalGateData: {
            message:
              "Escalated ticket from {customer_name}: {ticket_subject}\\nUrgency: {urgency} | Category: {category}\\nSuggested response: {suggested_response}",
          },
        },
        460,
      ),
      emailNode(
        "8",
        "Send AI-drafted response",
        1480,
        "{customer_email}",
        "Re: {ticket_subject}",
        "<p>Hi {customer_name},</p><p>{suggested_response}</p><p>Best regards,<br>Support Team</p>",
      ),
      slackNode(
        "9",
        "Log to #support-activity",
        1480,
        "📋 Ticket triaged: {ticket_subject}\\nCustomer: {customer_name} | Urgency: {urgency} | Category: {category} | Sentiment: {sentiment}",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"), // critical -> Slack alert
      e("4", "6", "output_2"), // not critical -> check escalation
      e("5", "8"), // after critical alert -> send response
      e("6", "7"), // needs escalation -> approval gate
      e("6", "8", "output_2"), // no escalation -> auto-send response
      e("7", "8"), // after approval -> send response
      e("8", "9"), // after sending -> log activity
    ],
    summary: `**Customer Support Triage** — AI-powered ticket classification and routing!\n\n1. New support ticket arrives from any helpdesk (Zendesk, Freshdesk, Intercom)\n2. **AI extracts ticket details** from the raw webhook payload\n3. **AI classifies urgency** (critical/high/medium/low), category, and sentiment\n4. Critical tickets → instant Slack alert to #critical-support\n5. Non-critical but complex → manager approval gate before responding\n6. AI drafts a response and sends to customer after appropriate review\n7. All tickets logged to #support-activity for tracking\n\n**To activate:** Say "connect openai", "connect slack", "connect email"`,
  },

  // 17. NPS Survey Automation (9 nodes)
  "NPS Survey Automation": {
    nodes: [
      trigger("Webhook: NPS response submitted", "webhook_app", {
        source: "Outgrow Forms / Delighted / SurveyMonkey",
        event: "response.completed",
        howItStarts: "A customer submits an NPS survey response",
        setup:
          "In your survey tool (Outgrow Forms, Delighted, SurveyMonkey) go to Integrations -> Webhooks -> paste your workflow URL. The AI will read the response data regardless of format.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract NPS data",
        280,
        "Read this NPS survey webhook payload. Different tools send data differently. Find the respondent email, respondent name, NPS score (0-10), and any open-ended feedback text. Respond JSON: {respondent_email: string, respondent_name: string, nps_score: number, feedback_text: string}",
        "{webhook_payload}",
        [
          { field: "respondent_email", as: "respondent_email", type: "string" },
          { field: "respondent_name", as: "respondent_name", type: "string" },
          { field: "nps_score", as: "nps_score", type: "number" },
          { field: "feedback_text", as: "feedback_text", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "AI analyze sentiment and themes",
        520,
        'Analyze this NPS feedback. The NPS score is {nps_score}. Categorize the respondent: 0-6 = detractor, 7-8 = passive, 9-10 = promoter. Extract key themes from the feedback. Respond JSON: {nps_category: \\"promoter\\" | \\"passive\\" | \\"detractor\\", key_themes: string, improvement_suggestion: string, should_follow_up: \\"true\\" | \\"false\\", follow_up_reason: string}',
        "Score: {nps_score}\\nFeedback: {feedback_text}\\nCustomer: {respondent_name}",
        [
          { field: "nps_category", as: "nps_category", type: "string" },
          { field: "key_themes", as: "key_themes", type: "string" },
          {
            field: "improvement_suggestion",
            as: "improvement_suggestion",
            type: "string",
          },
          { field: "should_follow_up", as: "should_follow_up", type: "string" },
          { field: "follow_up_reason", as: "follow_up_reason", type: "string" },
        ],
      ),
      n("4", "condition", "Is respondent a detractor? (score <= 6)", 760, {
        conditionData: [
          {
            variable: "nps_category",
            value: "detractor",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #cs-team: Detractor found",
        1000,
        "⚠️ *NPS Detractor Alert*\\nCustomer: {respondent_name} ({respondent_email})\\nScore: {nps_score}/10\\nFeedback: {feedback_text}\\nThemes: {key_themes}\\nSuggestion: {improvement_suggestion}",
      ),
      n(
        "6",
        "condition",
        "Is respondent a promoter? (score >= 9)",
        1000,
        {
          conditionData: [
            {
              variable: "nps_category",
              value: "promoter",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
        460,
      ),
      emailNode(
        "7",
        "Thank promoter and ask for review",
        1240,
        "{respondent_email}",
        "Thank you for the amazing feedback, {respondent_name}!",
        "<p>Hi {respondent_name},</p><p>We're thrilled you rated us {nps_score}/10! Your feedback means the world to us.</p><p>Would you mind sharing your experience on G2 or Capterra? It helps others discover us.</p><p><a href='https://www.g2.com/products/YOUR_PRODUCT/reviews'>Leave a review on G2</a></p><p>Thank you!</p>",
        460,
      ),
      n("8", "hitApi", "Log NPS data to Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/NPS_Data:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{respondent_name}","{respondent_email}","{nps_score}","{nps_category}","{key_themes}","{feedback_text}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
      emailNode(
        "9",
        "Send follow-up to passive respondents",
        1240,
        "{respondent_email}",
        "We'd love to do better, {respondent_name}",
        "<p>Hi {respondent_name},</p><p>Thank you for your feedback (score: {nps_score}/10). We noticed some areas we can improve:</p><p>{improvement_suggestion}</p><p>Would you be open to a quick call to discuss how we can better serve you?</p><p>Best regards,<br>Customer Success Team</p>",
        860,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "8"),
      e("4", "5"), // detractor -> Slack alert
      e("4", "6", "output_2"), // not detractor -> check promoter
      e("5", "8"), // detractor also logged
      e("6", "7"), // promoter -> ask for review
      e("6", "9", "output_2"), // passive -> follow-up email
    ],
    summary: `**NPS Survey Automation** — Complete NPS response pipeline!\n\n1. NPS survey response arrives (Outgrow Forms, Delighted, SurveyMonkey)\n2. **AI extracts** respondent data and score from raw webhook\n3. **AI analyzes sentiment**, key themes, and generates improvement suggestions\n4. Detractors (0-6) → immediate Slack alert to CS team\n5. Promoters (9-10) → thank-you email + G2/Capterra review request\n6. Passives (7-8) → personalized follow-up email with improvement plan\n7. All responses logged to Google Sheets for trending\n\n**To activate:** Say "connect openai", "connect slack", "connect email", "connect google sheets"`,
  },

  // 18. Customer Health Score (8 nodes)
  "Customer Health Score": {
    nodes: [
      trigger("Schedule: Daily health score check", "schedule", {
        schedule: "Every weekday at 8 AM",
        howItStarts:
          "Runs daily to compute health scores for all active customers",
        setup:
          "Set the schedule to run daily. Connect your CRM and product analytics APIs. The workflow pulls usage data and computes health scores.",
        inputFields: [
          { name: "company_id", type: "string", example: "comp_12345" },
        ],
      }),
      n("2", "hitApi", "Fetch customer data from CRM", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/companies/{company_id}?properties=name,last_activity_date,deal_amount,lifecycle_stage,num_associated_contacts",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "properties.last_activity_date",
              customName: "last_activity_date",
              type: "string",
            },
            {
              name: "properties.deal_amount",
              customName: "deal_amount",
              type: "string",
            },
            {
              name: "properties.num_associated_contacts",
              customName: "num_contacts",
              type: "number",
            },
          ],
        },
      }),
      n("3", "hitApi", "Fetch product usage metrics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.mixpanel.com/engage?filter={company_id}",
          headers: [{ key: "Authorization", value: "Bearer {mixpanel_token}" }],
          body: [],
          variables: [
            {
              name: "results[0].login_count_30d",
              customName: "login_count_30d",
              type: "number",
            },
            {
              name: "results[0].feature_adoption_pct",
              customName: "feature_adoption_pct",
              type: "number",
            },
            {
              name: "results[0].support_tickets_30d",
              customName: "support_tickets_30d",
              type: "number",
            },
            {
              name: "results[0].last_login_days_ago",
              customName: "last_login_days_ago",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI compute health score",
        760,
        'Calculate a customer health score from 0-100. Factors: login frequency (30%), feature adoption (25%), recency of last login (20%), support ticket volume (15% — more tickets = lower score), deal size (10% — higher deals = higher stakes). Respond JSON: {health_score: number, risk_level: \\"healthy\\" | \\"at_risk\\" | \\"critical\\", top_concern: string, recommended_action: string}',
        "Company: {company_name}\\nLogins (30d): {login_count_30d}\\nFeature adoption: {feature_adoption_pct}%\\nLast login: {last_login_days_ago} days ago\\nSupport tickets (30d): {support_tickets_30d}\\nDeal amount: {deal_amount}\\nContacts: {num_contacts}",
        [
          { field: "health_score", as: "health_score", type: "number" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "top_concern", as: "top_concern", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),
      n("5", "condition", "Is customer at risk? (score < 50)", 1000, {
        conditionData: [
          {
            variable: "health_score",
            value: "50",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "6",
        "Alert #cs-team: At-risk customer",
        1240,
        "🔴 *At-Risk Customer Alert*\\nCompany: {company_name}\\nHealth Score: {health_score}/100 ({risk_level})\\nTop Concern: {top_concern}\\nRecommended Action: {recommended_action}\\nLogins (30d): {login_count_30d} | Feature Adoption: {feature_adoption_pct}%",
      ),
      n(
        "7",
        "hitApi",
        "Update health score in CRM",
        1240,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/companies/{company_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"health_score":"{health_score}","risk_level":"{risk_level}","last_health_check":"today"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_company_id", type: "string" },
            ],
          },
        },
        460,
      ),
      n("8", "hitApi", "Log health score to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Health_Scores:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{company_name}","{health_score}","{risk_level}","{top_concern}","{recommended_action}","{login_count_30d}","{feature_adoption_pct}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "7"),
      e("5", "6"), // at risk -> Slack alert
      e("5", "8", "output_2"), // healthy -> just log
      e("6", "8"), // after alert -> log
      e("7", "8"), // after CRM update -> log
    ],
    summary: `**Customer Health Score** — AI-powered daily health monitoring!\n\n1. Runs on a daily schedule for each active customer\n2. Pulls CRM data from HubSpot (deal size, activity, contacts)\n3. Pulls product usage metrics from Mixpanel (logins, feature adoption, tickets)\n4. **AI computes a health score** (0-100) using weighted factors\n5. At-risk customers (score < 50) → instant Slack alert with recommended actions\n6. Health score written back to CRM for CSM dashboards\n7. All scores logged to Google Sheets for trend analysis\n\n**To activate:** Say "connect openai", "connect hubspot", "connect mixpanel", "connect slack", "connect google sheets"`,
  },

  // 19. Churn Risk Detector (8 nodes)
  "Churn Risk Detector": {
    nodes: [
      trigger("Schedule: Weekly churn analysis", "schedule", {
        schedule: "Every Monday at 7 AM",
        howItStarts: "Runs weekly to identify customers showing churn signals",
        setup:
          "Set to run every Monday. Connect your subscription management (Stripe) and product analytics. The workflow identifies declining engagement patterns.",
        inputFields: [
          { name: "customer_id", type: "string", example: "cus_abc123" },
        ],
      }),
      n("2", "hitApi", "Fetch subscription data from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/customers/{customer_id}/subscriptions",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].plan.amount",
              customName: "mrr_cents",
              type: "number",
            },
            {
              name: "data[0].current_period_end",
              customName: "renewal_date",
              type: "string",
            },
            {
              name: "data[0].status",
              customName: "subscription_status",
              type: "string",
            },
            {
              name: "data[0].cancel_at_period_end",
              customName: "cancel_pending",
              type: "string",
            },
          ],
        },
      }),
      n("3", "hitApi", "Fetch usage trends from analytics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.amplitude.com/2/usersearch?user={customer_id}",
          headers: [{ key: "Authorization", value: "Bearer {amplitude_key}" }],
          body: [],
          variables: [
            {
              name: "userData.login_trend",
              customName: "login_trend",
              type: "string",
            },
            {
              name: "userData.sessions_this_week",
              customName: "sessions_this_week",
              type: "number",
            },
            {
              name: "userData.sessions_last_week",
              customName: "sessions_last_week",
              type: "number",
            },
            {
              name: "userData.days_since_last_active",
              customName: "days_inactive",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "4",
        "AI assess churn probability",
        760,
        'Assess churn risk for this customer. Key signals: declining logins, pending cancellation, long inactivity, approaching renewal with low engagement. Respond JSON: {churn_probability: number, churn_risk: \\"high\\" | \\"medium\\" | \\"low\\", warning_signals: string, save_strategy: string, urgency_days: number}',
        "MRR: ${mrr_cents/100}\\nRenewal: {renewal_date}\\nStatus: {subscription_status}\\nCancel pending: {cancel_pending}\\nSessions this week: {sessions_this_week}\\nSessions last week: {sessions_last_week}\\nDays inactive: {days_inactive}\\nLogin trend: {login_trend}",
        [
          {
            field: "churn_probability",
            as: "churn_probability",
            type: "number",
          },
          { field: "churn_risk", as: "churn_risk", type: "string" },
          { field: "warning_signals", as: "warning_signals", type: "string" },
          { field: "save_strategy", as: "save_strategy", type: "string" },
          { field: "urgency_days", as: "urgency_days", type: "number" },
        ],
      ),
      n("5", "condition", "High churn risk? (probability > 70)", 1000, {
        conditionData: [
          {
            variable: "churn_probability",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "6",
        "Alert #churn-watch: High risk",
        1240,
        "🔥 *HIGH CHURN RISK*\\nCustomer: {customer_id}\\nChurn Probability: {churn_probability}%\\nMRR at risk: ${mrr_cents}\\nWarning signals: {warning_signals}\\nSave strategy: {save_strategy}\\nUrgency: Act within {urgency_days} days",
      ),
      n("7", "approvalGate", "CSM reviews save strategy", 1480, {
        approvalGateData: {
          message:
            "High churn risk for customer {customer_id}\\nChurn probability: {churn_probability}%\\nSave strategy: {save_strategy}\\n\\nApprove to execute the save outreach?",
        },
      }),
      emailNode(
        "8",
        "Send retention outreach",
        1720,
        "{customer_email}",
        "We miss you — let's chat about your experience",
        "<p>Hi there,</p><p>We noticed it's been a while since you've been active, and we want to make sure you're getting the most out of our platform.</p><p>{save_strategy}</p><p>Would you have 15 minutes for a quick call this week? I'd love to hear your feedback and see how we can help.</p><p>Best regards,<br>Customer Success Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"), // high risk -> Slack
      e("5", "8", "output_2"), // low risk -> skip to general outreach
      e("6", "7"), // after alert -> approval
      e("7", "8"), // after approval -> send email
    ],
    summary: `**Churn Risk Detector** — AI-powered weekly churn analysis!\n\n1. Runs every Monday to analyze customer engagement\n2. Pulls subscription data from Stripe (MRR, renewal date, cancel status)\n3. Pulls usage trends from Amplitude (sessions, login trends, inactivity)\n4. **AI assesses churn probability** and generates a save strategy\n5. High-risk customers (>70%) → Slack alert to #churn-watch\n6. CSM reviews and approves the save strategy via approval gate\n7. Personalized retention outreach email sent after approval\n\n**To activate:** Say "connect openai", "connect stripe", "connect amplitude", "connect slack", "connect email"`,
  },

  // 20. Customer Win-Back Campaign (8 nodes)
  "Customer Win-Back Campaign": {
    nodes: [
      trigger("Webhook: Subscription canceled", "webhook_app", {
        source: "Stripe",
        event: "customer.subscription.deleted",
        howItStarts: "A customer cancels their subscription in Stripe",
        setup:
          "In Stripe Dashboard -> Developers -> Webhooks -> Add endpoint -> paste your workflow URL -> select 'customer.subscription.deleted' event.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract cancellation details",
        280,
        "Read this Stripe subscription cancellation webhook. Extract the customer ID, customer email, plan name, monthly amount, cancellation reason if available, and how long they were subscribed. Respond JSON: {customer_id: string, customer_email: string, customer_name: string, plan_name: string, monthly_amount: string, cancellation_reason: string, months_subscribed: number}",
        "{webhook_payload}",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "monthly_amount", as: "monthly_amount", type: "string" },
          {
            field: "cancellation_reason",
            as: "cancellation_reason",
            type: "string",
          },
          {
            field: "months_subscribed",
            as: "months_subscribed",
            type: "number",
          },
        ],
      ),
      aiNode(
        "3",
        "AI craft win-back strategy",
        520,
        'Create a win-back strategy for this churned customer. Consider their tenure, plan, and cancellation reason. Suggest a personalized offer (discount, feature unlock, or consultation). Respond JSON: {win_back_offer: string, email_subject: string, email_body: string, offer_type: \\"discount\\" | \\"feature\\" | \\"consultation\\", discount_pct: number, win_back_likelihood: \\"high\\" | \\"medium\\" | \\"low\\"}',
        "Customer: {customer_name}\\nPlan: {plan_name} (${monthly_amount}/mo)\\nTenure: {months_subscribed} months\\nReason: {cancellation_reason}",
        [
          { field: "win_back_offer", as: "win_back_offer", type: "string" },
          { field: "email_subject", as: "winback_subject", type: "string" },
          { field: "email_body", as: "winback_body", type: "string" },
          { field: "offer_type", as: "offer_type", type: "string" },
          { field: "discount_pct", as: "discount_pct", type: "number" },
          {
            field: "win_back_likelihood",
            as: "win_back_likelihood",
            type: "string",
          },
        ],
      ),
      slackNode(
        "4",
        "Notify #cs-team of churn",
        760,
        "😔 *Customer Churned*\\nName: {customer_name} ({customer_email})\\nPlan: {plan_name} (${monthly_amount}/mo)\\nTenure: {months_subscribed} months\\nReason: {cancellation_reason}\\n\\nWin-back plan: {win_back_offer} (likelihood: {win_back_likelihood})",
      ),
      n("5", "delayAction", "Wait 3 days before outreach", 1000, {
        delayAction: { time: 3, timeFormat: "days" },
      }),
      n("6", "approvalGate", "CS manager approves win-back offer", 1240, {
        approvalGateData: {
          message:
            "Win-back campaign for {customer_name}:\\nOffer: {win_back_offer}\\nType: {offer_type} ({discount_pct}% off)\\nLikelihood: {win_back_likelihood}\\n\\nApprove to send?",
        },
      }),
      emailNode(
        "7",
        "Send win-back email",
        1480,
        "{customer_email}",
        "{winback_subject}",
        "<p>{winback_body}</p>",
      ),
      n("8", "hitApi", "Log win-back attempt in CRM", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/notes",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_note_body":"Win-back campaign sent. Offer: {win_back_offer}. Type: {offer_type}. Discount: {discount_pct}%. Likelihood: {win_back_likelihood}.","hs_timestamp":"now"}',
            },
            {
              key: "associations",
              value:
                '[{"to":{"id":"{customer_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":202}]}]',
            },
          ],
          variables: [{ name: "id", customName: "note_id", type: "string" }],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Customer Win-Back Campaign** — AI-powered churned customer recovery!\n\n1. Stripe fires a webhook when a subscription is canceled\n2. **AI extracts** cancellation details (plan, tenure, reason)\n3. **AI crafts a personalized win-back strategy** with tailored offers\n4. CS team notified on Slack immediately\n5. 3-day cooling-off period before outreach\n6. CS manager reviews and approves the win-back offer\n7. Personalized win-back email sent with the approved offer\n8. Attempt logged in HubSpot CRM for tracking\n\n**To activate:** Say "connect openai", "connect stripe", "connect slack", "connect email", "connect hubspot"`,
  },

  // ===================================================================
  // E-COMMERCE (21-25)
  // ===================================================================

  // 21. Abandoned Cart Recovery (8 nodes)
  "Abandoned Cart Recovery": {
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "Shopify",
        event: "carts/update",
        howItStarts:
          "Shopify detects a cart has been abandoned (no checkout within threshold)",
        setup:
          "In Shopify Admin -> Settings -> Notifications -> Webhooks -> Create webhook -> select 'Cart update' -> paste your workflow URL. Also configure abandoned cart timing in Shopify settings.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract cart details",
        280,
        "Read this Shopify abandoned cart webhook. Extract the customer email, customer first name, cart items (product names and prices), cart total, currency, and abandoned cart URL. Respond JSON: {customer_email: string, customer_name: string, cart_items: string, cart_total: string, currency: string, cart_url: string, item_count: number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "cart_items", as: "cart_items", type: "string" },
          { field: "cart_total", as: "cart_total", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          { field: "cart_url", as: "cart_url", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
        ],
      ),
      n("3", "delayAction", "Wait 1 hour", 520, {
        delayAction: { time: 1, timeFormat: "hours" },
      }),
      aiNode(
        "4",
        "AI write recovery email",
        760,
        'Write a friendly, non-pushy abandoned cart recovery email. Mention the specific items they left behind. Include urgency without being aggressive. Respond JSON: {subject: string, body_html: string, has_high_value_items: \\"true\\" | \\"false\\"}',
        "Customer: {customer_name}\\nItems: {cart_items}\\nTotal: {cart_total} {currency}\\nCart URL: {cart_url}",
        [
          { field: "subject", as: "recovery_subject", type: "string" },
          { field: "body_html", as: "recovery_body", type: "string" },
          {
            field: "has_high_value_items",
            as: "has_high_value_items",
            type: "string",
          },
        ],
      ),
      emailNode(
        "5",
        "Send recovery email #1",
        1000,
        "{customer_email}",
        "{recovery_subject}",
        "{recovery_body}",
      ),
      n("6", "delayAction", "Wait 24 hours", 1240, {
        delayAction: { time: 24, timeFormat: "hours" },
      }),
      n("7", "condition", "High-value cart? (> threshold)", 1480, {
        conditionData: [
          {
            variable: "has_high_value_items",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "8",
        "Send discount offer for high-value carts",
        1720,
        "{customer_email}",
        "Here's 10% off your cart, {customer_name}",
        "<p>Hi {customer_name},</p><p>We noticed you left some great items behind:</p><p>{cart_items}</p><p>Here's an exclusive <strong>10% discount</strong> just for you. Use code <strong>COMEBACK10</strong> at checkout.</p><p><a href='{cart_url}'>Complete your order →</a></p><p>Hurry — this offer expires in 48 hours!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"), // high value -> discount email
      // low value carts end after first recovery email
    ],
    summary: `**Abandoned Cart Recovery** — AI-powered multi-step cart recovery!\n\n1. Shopify webhook fires when a cart is abandoned\n2. **AI extracts** cart details (items, total, customer info)\n3. 1-hour delay to avoid being too aggressive\n4. **AI writes a personalized** recovery email mentioning specific items\n5. First recovery email sent\n6. 24-hour wait, then checks cart value\n7. High-value carts → second email with 10% discount code\n8. Standard carts → campaign ends after first email\n\n**To activate:** Say "connect openai", "connect shopify", "connect email"`,
  },

  // 22. New Order Notification (8 nodes)
  "New Order Notification": {
    nodes: [
      trigger("Webhook: New order placed", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts: "A customer places a new order on your Shopify store",
        setup:
          "In Shopify Admin -> Settings -> Notifications -> Webhooks -> Create webhook -> select 'Order creation' -> paste your workflow URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract order details",
        280,
        'Read this Shopify order webhook. Extract: customer name, customer email, order number, line items with quantities and prices, order total, shipping address (city, state, country), payment status, and whether it contains any items flagged as high-value (over $500). Respond JSON: {customer_name: string, customer_email: string, order_number: string, line_items: string, order_total: string, shipping_city: string, shipping_country: string, payment_status: string, is_high_value: \\"true\\" | \\"false\\"}',
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "shipping_city", as: "shipping_city", type: "string" },
          { field: "shipping_country", as: "shipping_country", type: "string" },
          { field: "payment_status", as: "payment_status", type: "string" },
          { field: "is_high_value", as: "is_high_value", type: "string" },
        ],
      ),
      slackNode(
        "3",
        "Notify #orders channel",
        520,
        "🛒 *New Order #{order_number}*\\nCustomer: {customer_name}\\nTotal: {order_total}\\nItems: {line_items}\\nShipping to: {shipping_city}, {shipping_country}\\nPayment: {payment_status}",
      ),
      emailNode(
        "4",
        "Send order confirmation to customer",
        760,
        "{customer_email}",
        "Order #{order_number} confirmed — thank you, {customer_name}!",
        "<p>Hi {customer_name},</p><p>Thank you for your order!</p><p><strong>Order #{order_number}</strong></p><p>{line_items}</p><p><strong>Total: {order_total}</strong></p><p>Shipping to: {shipping_city}, {shipping_country}</p><p>We'll send you a tracking number as soon as your order ships.</p>",
      ),
      n("5", "condition", "Is this a high-value order?", 1000, {
        conditionData: [
          {
            variable: "is_high_value",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      n("6", "hitApi", "Create VIP note in CRM", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/notes",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_note_body":"VIP Order #{order_number} — {order_total}. Items: {line_items}. Ensure white-glove fulfillment.","hs_timestamp":"now"}',
            },
          ],
          variables: [{ name: "id", customName: "note_id", type: "string" }],
        },
      }),
      slackNode(
        "7",
        "Alert #vip-orders for high-value",
        1240,
        "💎 *VIP ORDER #{order_number}*\\nCustomer: {customer_name} ({customer_email})\\nTotal: {order_total}\\nItems: {line_items}\\n\\n→ White-glove fulfillment required!",
        460,
      ),
      n("8", "hitApi", "Log order to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Orders:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{order_number}","{customer_name}","{customer_email}","{order_total}","{line_items}","{shipping_city}","{shipping_country}","{payment_status}","{is_high_value}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("2", "4"),
      e("4", "5"),
      e("5", "6"), // high value -> CRM note
      e("5", "8", "output_2"), // normal -> log to sheets
      e("6", "7"), // CRM note -> VIP Slack
      e("7", "8"), // VIP Slack -> log to sheets
      e("3", "8"), // after regular Slack -> log
    ],
    summary: `**New Order Notification** — Complete order processing pipeline!\n\n1. Shopify webhook fires on new order creation\n2. **AI extracts** order details (items, total, shipping, value tier)\n3. Instant Slack notification to #orders channel\n4. Personalized order confirmation email sent to customer\n5. High-value orders (>$500) → CRM VIP note + #vip-orders Slack alert\n6. All orders logged to Google Sheets for analytics\n\n**To activate:** Say "connect openai", "connect shopify", "connect slack", "connect email", "connect hubspot", "connect google sheets"`,
  },

  // 23. Order Fraud Detector (8 nodes)
  "Order Fraud Detector": {
    nodes: [
      trigger("Webhook: New order for fraud check", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "A new order is placed and needs to be checked for fraud signals",
        setup:
          "In Shopify Admin -> Settings -> Notifications -> Webhooks -> Create webhook -> select 'Order creation' -> paste your workflow URL. This runs in parallel with your normal order flow.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract order + fraud signals",
        280,
        'Read this Shopify order webhook. Extract fraud-relevant details: customer email, customer name, order total, billing address, shipping address, IP country, whether billing and shipping match, number of previous orders from this customer, payment method, and any Shopify fraud analysis signals. Respond JSON: {customer_email: string, customer_name: string, order_number: string, order_total: string, billing_address: string, shipping_address: string, addresses_match: \\"true\\" | \\"false\\", ip_country: string, previous_orders: number, payment_method: string, shopify_risk_level: string}',
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "billing_address", as: "billing_address", type: "string" },
          { field: "shipping_address", as: "shipping_address", type: "string" },
          { field: "addresses_match", as: "addresses_match", type: "string" },
          { field: "ip_country", as: "ip_country", type: "string" },
          { field: "previous_orders", as: "previous_orders", type: "number" },
          { field: "payment_method", as: "payment_method", type: "string" },
          {
            field: "shopify_risk_level",
            as: "shopify_risk_level",
            type: "string",
          },
        ],
      ),
      aiNode(
        "3",
        "AI assess fraud risk",
        520,
        'Assess the fraud risk for this order. Red flags: mismatched billing/shipping addresses, new customer with high-value order, IP country different from shipping country, free email domain with high order value. Respond JSON: {fraud_score: number, risk_level: \\"high\\" | \\"medium\\" | \\"low\\", red_flags: string, recommendation: \\"approve\\" | \\"review\\" | \\"block\\", reasoning: string}',
        "Order #{order_number}: {order_total}\\nCustomer: {customer_name} ({customer_email})\\nBilling: {billing_address}\\nShipping: {shipping_address}\\nAddresses match: {addresses_match}\\nIP country: {ip_country}\\nPrevious orders: {previous_orders}\\nPayment: {payment_method}\\nShopify risk: {shopify_risk_level}",
        [
          { field: "fraud_score", as: "fraud_score", type: "number" },
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "red_flags", as: "red_flags", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "reasoning", as: "fraud_reasoning", type: "string" },
        ],
      ),
      n("4", "condition", "Fraud score high? (> 70)", 760, {
        conditionData: [
          {
            variable: "fraud_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #fraud-review: Suspicious order",
        1000,
        "🚨 *FRAUD ALERT — Order #{order_number}*\\nFraud Score: {fraud_score}/100 ({risk_level})\\nCustomer: {customer_name} ({customer_email})\\nTotal: {order_total}\\nRed Flags: {red_flags}\\nRecommendation: {recommendation}\\nReasoning: {fraud_reasoning}",
      ),
      n("6", "approvalGate", "Fraud team reviews order", 1240, {
        approvalGateData: {
          message:
            "Suspicious order #{order_number} needs review:\\nFraud Score: {fraud_score}/100\\nRed Flags: {red_flags}\\nTotal: {order_total}\\nCustomer: {customer_name}\\n\\nApprove to fulfill, or reject to cancel the order.",
        },
      }),
      n("7", "hitApi", "Update order tag in Shopify", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/orders/{order_number}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "order",
              value: '{"tags":"fraud_reviewed,score_{fraud_score}"}',
            },
          ],
          variables: [
            {
              name: "order.id",
              customName: "updated_order_id",
              type: "string",
            },
          ],
        },
      }),
      n(
        "8",
        "hitApi",
        "Log fraud check to Google Sheets",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Fraud_Log:append?valueInputOption=RAW",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{order_number}","{customer_name}","{customer_email}","{order_total}","{fraud_score}","{risk_level}","{red_flags}","{recommendation}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_added",
                type: "number",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"), // high fraud -> Slack alert
      e("4", "7", "output_2"), // low fraud -> tag and log
      e("5", "6"), // after alert -> approval gate
      e("6", "7"), // after approval -> update Shopify
      e("7", "8"), // after tag -> log
    ],
    summary: `**Order Fraud Detector** — AI-powered real-time fraud screening!\n\n1. Shopify webhook fires on every new order\n2. **AI extracts** order details and fraud-relevant signals\n3. **AI computes a fraud score** (0-100) based on address mismatch, IP location, order value, customer history\n4. High-risk orders (>70) → instant Slack alert to #fraud-review\n5. Fraud team reviews via approval gate before fulfillment\n6. Order tagged in Shopify with fraud review results\n7. All fraud checks logged to Google Sheets for pattern analysis\n\n**To activate:** Say "connect openai", "connect shopify", "connect slack", "connect google sheets"`,
  },

  // 24. Inventory Low-Stock Alert (8 nodes)
  "Inventory Low-Stock Alert": {
    nodes: [
      trigger("Schedule: Check inventory levels", "schedule", {
        schedule: "Every 4 hours",
        howItStarts:
          "Runs every 4 hours to check product inventory levels against reorder thresholds",
        setup:
          "Set schedule to run every 4 hours. Connect your Shopify store and configure reorder threshold (default: 10 units). The workflow checks all active products.",
        inputFields: [
          { name: "reorder_threshold", type: "number", example: "10" },
        ],
      }),
      n("2", "hitApi", "Fetch inventory levels from Shopify", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/inventory_levels.json?limit=250",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          ],
          body: [],
          variables: [
            {
              name: "inventory_levels",
              customName: "inventory_data",
              type: "string",
            },
            {
              name: "inventory_levels.length",
              customName: "total_products",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI identify low-stock items",
        520,
        "Analyze this inventory data. Identify all products where available quantity is below the reorder threshold of {reorder_threshold}. For each low-stock item, include product name, current stock, and location. Respond JSON: {low_stock_items: string, low_stock_count: number, most_critical_item: string, most_critical_qty: number, total_items_checked: number}",
        "Inventory data: {inventory_data}\\nReorder threshold: {reorder_threshold}",
        [
          { field: "low_stock_items", as: "low_stock_items", type: "string" },
          { field: "low_stock_count", as: "low_stock_count", type: "number" },
          {
            field: "most_critical_item",
            as: "most_critical_item",
            type: "string",
          },
          {
            field: "most_critical_qty",
            as: "most_critical_qty",
            type: "number",
          },
          {
            field: "total_items_checked",
            as: "total_items_checked",
            type: "number",
          },
        ],
      ),
      n("4", "condition", "Any items below threshold?", 760, {
        conditionData: [
          {
            variable: "low_stock_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #inventory: Low stock items",
        1000,
        "📦 *LOW STOCK ALERT*\\n{low_stock_count} items below threshold ({reorder_threshold} units):\\n{low_stock_items}\\n\\nMost critical: {most_critical_item} ({most_critical_qty} remaining)",
      ),
      emailNode(
        "6",
        "Email purchasing team",
        1240,
        "purchasing@company.com",
        "Low Stock Alert: {low_stock_count} items need reorder",
        "<h2>Low Stock Alert</h2><p><strong>{low_stock_count}</strong> products are below the reorder threshold of {reorder_threshold} units.</p><p><strong>Most Critical:</strong> {most_critical_item} ({most_critical_qty} remaining)</p><h3>All Low-Stock Items:</h3><p>{low_stock_items}</p><p>Total products checked: {total_items_checked}</p>",
      ),
      n("7", "condition", "Any item at zero stock?", 1480, {
        conditionData: [
          {
            variable: "most_critical_qty",
            value: "0",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "8",
        "URGENT: Out of stock alert",
        1720,
        "🔴 *OUT OF STOCK — URGENT*\\n{most_critical_item} has ZERO inventory!\\n\\nImmediate action required to prevent lost sales.\\nFull low-stock list: {low_stock_items}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"), // has low stock -> Slack
      e("5", "6"), // Slack -> email purchasing
      e("6", "7"), // check zero stock
      e("7", "8"), // zero stock -> urgent alert
    ],
    summary: `**Inventory Low-Stock Alert** — Automated inventory monitoring!\n\n1. Runs every 4 hours checking all product inventory levels\n2. Fetches current stock from Shopify inventory API\n3. **AI identifies** all items below reorder threshold\n4. Low-stock detected → Slack alert to #inventory channel\n5. Purchasing team emailed with full reorder list\n6. Zero-stock items → additional urgent Slack notification\n7. No low-stock items → workflow ends silently (no spam)\n\n**To activate:** Say "connect openai", "connect shopify", "connect slack", "connect email"`,
  },

  // 25. Returns Processing (9 nodes)
  "Returns Processing": {
    nodes: [
      trigger("Webhook: Return request submitted", "webhook_app", {
        source: "Shopify / Returns Portal",
        event: "returns/request",
        howItStarts: "A customer submits a return or exchange request",
        setup:
          "Connect your returns portal (Loop, Returnly, or custom) webhook, or use Shopify's return request webhook -> paste your workflow URL.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract return details",
        280,
        'Read this return request webhook. Extract: customer email, customer name, order number, items being returned (product names, quantities, prices), return reason, original order date, and whether the customer wants a refund or exchange. Respond JSON: {customer_email: string, customer_name: string, order_number: string, return_items: string, return_reason: string, return_total: string, order_date: string, return_type: \\"refund\\" | \\"exchange\\", item_count: number}',
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "return_items", as: "return_items", type: "string" },
          { field: "return_reason", as: "return_reason", type: "string" },
          { field: "return_total", as: "return_total", type: "string" },
          { field: "order_date", as: "order_date", type: "string" },
          { field: "return_type", as: "return_type", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
        ],
      ),
      aiNode(
        "3",
        "AI evaluate return eligibility",
        520,
        'Evaluate this return request. Check: is the return within the 30-day return window? Is the reason valid (defective, wrong item, not as described = always eligible; changed mind = eligible within window; worn/used = not eligible)? Respond JSON: {eligible: \\"true\\" | \\"false\\", eligibility_reason: string, needs_inspection: \\"true\\" | \\"false\\", auto_approve: \\"true\\" | \\"false\\", suggested_resolution: string}',
        "Order #{order_number} placed {order_date}\\nItems: {return_items}\\nReason: {return_reason}\\nType: {return_type}\\nTotal: {return_total}",
        [
          { field: "eligible", as: "eligible", type: "string" },
          {
            field: "eligibility_reason",
            as: "eligibility_reason",
            type: "string",
          },
          { field: "needs_inspection", as: "needs_inspection", type: "string" },
          { field: "auto_approve", as: "auto_approve", type: "string" },
          {
            field: "suggested_resolution",
            as: "suggested_resolution",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Is return eligible?", 760, {
        conditionData: [
          {
            variable: "eligible",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      n("5", "condition", "Can auto-approve? (no inspection needed)", 1000, {
        conditionData: [
          {
            variable: "auto_approve",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
      emailNode(
        "6",
        "Send return shipping label",
        1240,
        "{customer_email}",
        "Your return for order #{order_number} is approved",
        "<p>Hi {customer_name},</p><p>Your return request has been approved!</p><p><strong>Items:</strong> {return_items}</p><p><strong>Resolution:</strong> {suggested_resolution}</p><p>Please use the attached shipping label to send your items back. Once received, your {return_type} will be processed within 3-5 business days.</p><p><a href='https://returns.company.com/label/{order_number}'>Download Return Label</a></p>",
      ),
      n(
        "7",
        "approvalGate",
        "Warehouse team inspects return",
        1240,
        {
          approvalGateData: {
            message:
              "Return needs inspection:\\nOrder #{order_number} from {customer_name}\\nItems: {return_items}\\nReason: {return_reason}\\nResolution: {suggested_resolution}\\n\\nApprove after physical inspection.",
          },
        },
        460,
      ),
      emailNode(
        "8",
        "Deny return with explanation",
        1000,
        "{customer_email}",
        "Update on your return request for order #{order_number}",
        "<p>Hi {customer_name},</p><p>We've reviewed your return request for order #{order_number}.</p><p>Unfortunately, we're unable to process this return: {eligibility_reason}</p><p>If you believe this is an error, please reply to this email and our team will review your case personally.</p>",
        460,
      ),
      slackNode(
        "9",
        "Log return to #operations",
        1480,
        "📦 *Return Processed*\\nOrder #{order_number} | Customer: {customer_name}\\nItems: {return_items} | Reason: {return_reason}\\nType: {return_type} | Total: {return_total}\\nEligible: {eligible} | Auto-approved: {auto_approve}\\nResolution: {suggested_resolution}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"), // eligible -> check auto-approve
      e("4", "8", "output_2"), // not eligible -> deny email
      e("5", "6"), // auto-approve -> send label
      e("5", "7", "output_2"), // needs inspection -> approval gate
      e("6", "9"), // after label -> log
      e("7", "6"), // after inspection approval -> send label
      e("8", "9"), // after deny -> log
    ],
    summary: `**Returns Processing** — AI-powered automated returns workflow!\n\n1. Return request arrives via webhook (Shopify, Loop, Returnly)\n2. **AI extracts** return details (items, reason, refund/exchange preference)\n3. **AI evaluates eligibility** (30-day window, valid reason, inspection needed?)\n4. Eligible + simple returns → auto-approved, shipping label sent immediately\n5. Eligible + needs inspection → warehouse team approval gate\n6. Ineligible → polite denial email with explanation\n7. All returns logged to Slack #operations for tracking\n\n**To activate:** Say "connect openai", "connect shopify", "connect slack", "connect email"`,
  },

  // ===================================================================
  // FINANCE (26-28)
  // ===================================================================

  // 26. Invoice Reminder System (8 nodes)
  "Invoice Reminder System": {
    nodes: [
      trigger("Schedule: Daily overdue invoice check", "schedule", {
        schedule: "Every weekday at 9 AM",
        howItStarts: "Runs daily to check for overdue and upcoming invoices",
        setup:
          "Set schedule to run every weekday at 9 AM. Connect your invoicing system (Stripe, QuickBooks, Xero). The workflow fetches open invoices and sends reminders based on due dates.",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch open invoices from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/invoices?status=open&limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "open_invoices", type: "string" },
            {
              name: "data.length",
              customName: "invoice_count",
              type: "number",
            },
          ],
        },
      }),
      aiNode(
        "3",
        "AI categorize invoices by urgency",
        520,
        "Analyze these open invoices. Categorize each by urgency: 'overdue_critical' (30+ days past due), 'overdue' (1-29 days past due), 'due_soon' (due within 7 days), 'upcoming' (due in 7+ days). Summarize the totals. Respond JSON: {overdue_critical_count: number, overdue_critical_total: string, overdue_critical_invoices: string, overdue_count: number, overdue_total: string, overdue_invoices: string, due_soon_count: number, due_soon_invoices: string, total_outstanding: string}",
        "Open invoices: {open_invoices}",
        [
          {
            field: "overdue_critical_count",
            as: "overdue_critical_count",
            type: "number",
          },
          {
            field: "overdue_critical_total",
            as: "overdue_critical_total",
            type: "string",
          },
          {
            field: "overdue_critical_invoices",
            as: "overdue_critical_invoices",
            type: "string",
          },
          { field: "overdue_count", as: "overdue_count", type: "number" },
          { field: "overdue_total", as: "overdue_total", type: "string" },
          { field: "overdue_invoices", as: "overdue_invoices", type: "string" },
          { field: "due_soon_count", as: "due_soon_count", type: "number" },
          {
            field: "due_soon_invoices",
            as: "due_soon_invoices",
            type: "string",
          },
          {
            field: "total_outstanding",
            as: "total_outstanding",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Any critically overdue? (30+ days)", 760, {
        conditionData: [
          {
            variable: "overdue_critical_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      slackNode(
        "5",
        "Alert #finance: Critical overdue invoices",
        1000,
        "🔴 *CRITICAL OVERDUE INVOICES*\\n{overdue_critical_count} invoices 30+ days overdue totaling {overdue_critical_total}\\n\\n{overdue_critical_invoices}\\n\\nTotal outstanding: {total_outstanding}",
      ),
      emailNode(
        "6",
        "Send overdue reminder to customers",
        1000,
        "ar@company.com",
        "Action Required: {overdue_count} overdue invoices totaling {overdue_total}",
        "<h2>Overdue Invoice Summary</h2><p><strong>{overdue_count}</strong> invoices are past due, totaling <strong>{overdue_total}</strong>.</p><h3>Critically Overdue (30+ days):</h3><p>{overdue_critical_invoices}</p><h3>Recently Overdue:</h3><p>{overdue_invoices}</p><h3>Due Soon (next 7 days):</h3><p>{due_soon_invoices}</p><p>Total outstanding: <strong>{total_outstanding}</strong></p>",
        460,
      ),
      n("7", "approvalGate", "Finance reviews before escalation", 1240, {
        approvalGateData: {
          message:
            "Critical overdue invoices need escalation:\\n{overdue_critical_invoices}\\nTotal: {overdue_critical_total}\\n\\nApprove to send final demand notices to these customers.",
        },
      }),
      emailNode(
        "8",
        "Send final demand notice",
        1480,
        "ar@company.com",
        "FINAL NOTICE: Payment required — {overdue_critical_total} overdue",
        "<p>Dear Customer,</p><p>This is a <strong>final notice</strong> regarding your overdue invoice(s). The following amounts are now 30+ days past due:</p><p>{overdue_critical_invoices}</p><p>Total overdue: <strong>{overdue_critical_total}</strong></p><p>Please remit payment within 7 business days to avoid further action. If you've already sent payment, please disregard this notice.</p><p>Payment portal: <a href='https://billing.company.com/pay'>Pay Now</a></p><p>Finance Department</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("3", "6"),
      e("4", "5"), // critically overdue -> Slack alert
      e("4", "6", "output_2"), // not critical -> still send regular summary
      e("5", "7"), // after alert -> approval gate for escalation
      e("7", "8"), // after approval -> send final demand
    ],
    summary: `**Invoice Reminder System** — Automated accounts receivable management!\n\n1. Runs daily at 9 AM checking all open invoices in Stripe\n2. **AI categorizes** invoices: critically overdue (30+ days), overdue, due soon, upcoming\n3. Critically overdue → Slack alert to #finance channel\n4. Finance team gets daily summary email with all overdue invoices\n5. Finance reviews and approves escalation via approval gate\n6. Approved → final demand notices sent to chronically overdue customers\n7. Payment portal link included for easy remittance\n\n**To activate:** Say "connect openai", "connect stripe", "connect slack", "connect email"`,
  },

  // 27. Expense Report Processor (8 nodes)
  "Expense Report Processor": {
    nodes: [
      trigger("Webhook: Expense report submitted", "webhook_app", {
        source: "Expensify / SAP Concur / Custom Form",
        event: "report.submitted",
        howItStarts: "An employee submits an expense report for approval",
        setup:
          "In your expense tool (Expensify, SAP Concur) go to Admin -> Webhooks -> paste your workflow URL. Or connect your internal expense submission form.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI extract expense details",
        280,
        "Read this expense report webhook payload. Extract: submitter name, submitter email, submitter department, report title, total amount, currency, number of line items, line item details (description, amount, category, date), and any attached receipt count. Respond JSON: {submitter_name: string, submitter_email: string, department: string, report_title: string, total_amount: number, currency: string, line_items: string, line_item_count: number, receipt_count: number}",
        "{webhook_payload}",
        [
          { field: "submitter_name", as: "submitter_name", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "report_title", as: "report_title", type: "string" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "line_item_count", as: "line_item_count", type: "number" },
          { field: "receipt_count", as: "receipt_count", type: "number" },
        ],
      ),
      aiNode(
        "3",
        "AI audit expense report",
        520,
        'Audit this expense report for policy compliance. Check for: missing receipts (every item over $25 needs a receipt), duplicate expenses, unusually high amounts for the category (meals > $100, transport > $200, lodging > $400 per night), weekend expenses without justification, and overall reasonableness. Respond JSON: {compliance_status: \\"compliant\\" | \\"needs_review\\" | \\"flagged\\", issues_found: string, issue_count: number, auto_approve_eligible: \\"true\\" | \\"false\\", risk_score: number}',
        "Submitter: {submitter_name} ({department})\\nReport: {report_title}\\nTotal: {total_amount} {currency}\\nLine items: {line_items}\\nReceipts attached: {receipt_count} of {line_item_count} items",
        [
          {
            field: "compliance_status",
            as: "compliance_status",
            type: "string",
          },
          { field: "issues_found", as: "issues_found", type: "string" },
          { field: "issue_count", as: "issue_count", type: "number" },
          {
            field: "auto_approve_eligible",
            as: "auto_approve_eligible",
            type: "string",
          },
          { field: "risk_score", as: "risk_score", type: "number" },
        ],
      ),
      n(
        "4",
        "condition",
        "Auto-approve eligible? (compliant + low amount)",
        760,
        {
          conditionData: [
            {
              variable: "auto_approve_eligible",
              value: "true",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),
      emailNode(
        "5",
        "Notify submitter: Auto-approved",
        1000,
        "{submitter_email}",
        "Expense report approved: {report_title}",
        "<p>Hi {submitter_name},</p><p>Your expense report <strong>{report_title}</strong> for {total_amount} {currency} has been automatically approved.</p><p>Reimbursement will be processed in your next pay cycle.</p>",
      ),
      n(
        "6",
        "approvalGate",
        "Manager reviews flagged report",
        1000,
        {
          approvalGateData: {
            message:
              "Expense report needs review:\\nSubmitter: {submitter_name} ({department})\\nReport: {report_title}\\nTotal: {total_amount} {currency}\\nIssues: {issues_found}\\nRisk Score: {risk_score}/100\\n\\nApprove or reject this expense report.",
          },
        },
        460,
      ),
      slackNode(
        "7",
        "Notify #finance of processing",
        1240,
        "💰 *Expense Report Processed*\\nSubmitter: {submitter_name} ({department})\\nReport: {report_title}\\nTotal: {total_amount} {currency}\\nStatus: {compliance_status}\\nIssues: {issue_count}\\nAuto-approved: {auto_approve_eligible}",
      ),
      n("8", "hitApi", "Log to accounting system", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.xero.com/api.xro/2.0/ExpenseClaims",
          headers: [
            { key: "Authorization", value: "Bearer {xero_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "User", value: '{"FirstName":"{submitter_name}"}' },
            {
              key: "Receipts",
              value:
                '[{"Date":"today","Total":"{total_amount}","Description":"{report_title}"}]',
            },
          ],
          variables: [
            {
              name: "ExpenseClaims[0].ExpenseClaimID",
              customName: "claim_id",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"), // auto-approve -> notify submitter
      e("4", "6", "output_2"), // needs review -> manager approval gate
      e("5", "7"), // after approval notification -> Slack log
      e("6", "7"), // after manager decision -> Slack log
      e("7", "8"), // after Slack -> log to accounting
    ],
    summary: `**Expense Report Processor** — AI-powered expense auditing and approval!\n\n1. Employee submits expense report (Expensify, SAP Concur, custom form)\n2. **AI extracts** all line items, amounts, categories, and receipt info\n3. **AI audits** for policy compliance (missing receipts, high amounts, duplicates)\n4. Compliant + low-risk → auto-approved, employee notified instantly\n5. Flagged reports → manager approval gate with detailed issue list\n6. Processing logged to Slack #finance for visibility\n7. Approved expenses pushed to Xero accounting system\n\n**To activate:** Say "connect openai", "connect slack", "connect email", "connect xero"`,
  },

  // 28. Recurring Invoice Generator (8 nodes)
  "Recurring Invoice Generator": {
    nodes: [
      trigger("Schedule: Monthly invoice generation", "schedule", {
        schedule: "1st of every month at 6 AM",
        howItStarts:
          "Runs on the 1st of each month to generate invoices for all active subscriptions",
        setup:
          "Set schedule to run on the 1st of every month. Connect your CRM for client data and Stripe/QuickBooks for invoice generation. Maintains a client list with billing details.",
        inputFields: [],
      }),
      n("2", "hitApi", "Fetch active clients from CRM", 280, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.hubapi.com/crm/v3/objects/companies?properties=name,billing_email,billing_amount,billing_frequency,contract_start,payment_terms&filterGroups=[{"filters":[{"propertyName":"lifecycle_stage","operator":"EQ","value":"customer"}]}]&limit=100',
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            { name: "results", customName: "active_clients", type: "string" },
            { name: "total", customName: "client_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "AI prepare invoice batch",
        520,
        "Analyze this list of active clients and prepare an invoice batch for the current billing cycle. For each client, calculate the amount due based on their billing frequency and contract terms. Flag any clients with special terms or overdue balances. Respond JSON: {invoices_to_generate: string, invoice_count: number, total_billing_amount: string, flagged_clients: string, flagged_count: number}",
        "Active clients: {active_clients}\\nBilling period: Current month\\nClient count: {client_count}",
        [
          {
            field: "invoices_to_generate",
            as: "invoices_to_generate",
            type: "string",
          },
          { field: "invoice_count", as: "invoice_count", type: "number" },
          {
            field: "total_billing_amount",
            as: "total_billing_amount",
            type: "string",
          },
          { field: "flagged_clients", as: "flagged_clients", type: "string" },
          { field: "flagged_count", as: "flagged_count", type: "number" },
        ],
      ),
      n("4", "condition", "Any flagged clients needing review?", 760, {
        conditionData: [
          {
            variable: "flagged_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),
      n("5", "approvalGate", "Finance reviews flagged invoices", 1000, {
        approvalGateData: {
          message:
            "Monthly invoice batch ready — {invoice_count} invoices totaling {total_billing_amount}\\n\\n{flagged_count} clients flagged for review:\\n{flagged_clients}\\n\\nApprove to generate all invoices (flagged clients will be included after approval).",
        },
      }),
      n("6", "hitApi", "Generate invoices in Stripe", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/invoices",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "auto_advance", value: "true" },
            { key: "collection_method", value: "send_invoice" },
            { key: "days_until_due", value: "30" },
          ],
          variables: [
            { name: "id", customName: "invoice_id", type: "string" },
            { name: "status", customName: "invoice_status", type: "string" },
          ],
        },
      }),
      slackNode(
        "7",
        "Notify #finance: Invoices generated",
        1480,
        "📄 *Monthly Invoice Batch Complete*\\nGenerated: {invoice_count} invoices\\nTotal billing: {total_billing_amount}\\nFlagged for review: {flagged_count}\\nStatus: All invoices sent to customers\\n\\nInvoices will auto-collect via Stripe after 30-day terms.",
      ),
      n("8", "hitApi", "Log invoice batch to Google Sheets", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Invoice_Batches:append?valueInputOption=RAW",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["current_month","{invoice_count}","{total_billing_amount}","{flagged_count}","{flagged_clients}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_added",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"), // flagged clients -> approval gate
      e("4", "6", "output_2"), // no flags -> generate directly
      e("5", "6"), // after approval -> generate invoices
      e("6", "7"), // after generation -> Slack notification
      e("7", "8"), // after Slack -> log to sheets
    ],
    summary: `**Recurring Invoice Generator** — Automated monthly billing pipeline!\n\n1. Runs on the 1st of every month at 6 AM\n2. Fetches all active clients from HubSpot CRM with billing details\n3. **AI prepares the invoice batch**, calculating amounts and flagging exceptions\n4. Flagged clients (special terms, overdue balances) → finance approval gate\n5. Clean invoices generated directly in Stripe with 30-day payment terms\n6. Finance team notified on Slack with batch summary\n7. Invoice batch logged to Google Sheets for audit trail\n\n**To activate:** Say "connect openai", "connect hubspot", "connect stripe", "connect slack", "connect google sheets"`,
  },
};
